import { ref, computed } from "vue";

import { useCurriculumStore } from "@/stores/curriculumStore";
import { useProgressStore } from "@/stores/progressStore";
import { useAssessmentStore } from "@/stores/assessmentStore";
import { useUserStore } from "@/stores/userStore";
import { useDailyStreakEngine } from "@/composables/useDailyStreakEngine";
import { questionApi } from "@/services/questionApi";
import { isLessonLocked } from "@/composables/usePlanGating";
import { useAnalytics } from "@/composables/useAnalytics";

// ── Scoring constants (previously in diagnostic-blueprint.json) ──
const SCORING = {
  easyCorrect: 0.5,
  mediumCorrect: 0.7,
  hardCorrect: 1.0,
  levelMapping: [
    { max: 0.4, level: "foundation", difficultyLevel: "easy" },
    { max: 0.9, level: "developing", difficultyLevel: "medium" },
    { max: 1.4, level: "secure",     difficultyLevel: "hard" },
    { max: 2.0, level: "advanced",   difficultyLevel: "hard" }
  ]
};

const EARLY_STOP_THRESHOLD = 90;
const ESTIMATED_DURATION_MINUTES = 30;

/**
 * Diagnostic Session
 * ------------------
 * Purpose:
 * - Estimate baseline ability across all curriculum topics
 * - Seed section-group priors (without reducing secure+ topics)
 * - Generate ability + module readiness
 *
 * Builds sequence from curriculumStore (no mock blueprint).
 * Every topic gets 1 medium question + 1 adaptive follow-up (hard if correct, easy if wrong).
 */
export function useDiagnosticSession(childId) {
  const curriculumStore = useCurriculumStore();
  const progressStore = useProgressStore();
  const assessmentStore = useAssessmentStore();
  const userStore = useUserStore();
  const streakEngine = useDailyStreakEngine();
  const { track } = useAnalytics();

  /* -----------------------------
     SESSION STATE
  ------------------------------ */
  const sessionStarted = ref(false);
  const sessionCompleted = ref(false);
  const confidence = ref(0);
  const questionSequence = ref([]);
  const currentIndex = ref(0);
  const currentQuestion = ref(null);
  const questionStartTime = ref(null);
  const responses = ref([]);
  // Pre-fetched question map: { [sgId]: { easy, medium, hard } }
  const diagnosticQuestionMap = ref({});

  /* -----------------------------
     1. Build sequence from curriculum
  ----------------------------- */
  function getStartDifficulty(sgId) {
    const hasPriorData = !!progressStore.progress?.[childId]?.[sgId];
    const band = progressStore.getSectionGroupBand(childId, sgId);
    // secure+ → hard; developing → medium; known beginner (from prior data) → easy
    // No prior data (first diagnostic) → medium so we get useful signal either way
    if (band === "secure" || band === "confident" || band === "mastery") return "hard";
    if (band === "developing") return "medium";
    if (hasPriorData) return "easy"; // confirmed beginner from prior sessions
    return "medium"; // fresh student — start at medium
  }

  function isSecureOrAbove(sgId) {
    const band = progressStore.getSectionGroupBand(childId, sgId);
    return band === "secure" || band === "confident" || band === "mastery";
  }

  function generateDiagnosticSequence() {
    const seq = [];

    for (const mod of curriculumStore.modules) {
      for (const unit of mod.units) {
        for (const lesson of unit.lessons) {
          const subsections = lesson.subsections || [];
          if (!subsections.length) continue;

          // Skip lessons locked for the student's current plan
          if (isLessonLocked(lesson.excludedPlans, userStore.effectivePlan)) continue;

          for (const sub of subsections) {
            const sgId = sub.content;
            if (!sgId) continue;

            const startDiff = getStartDifficulty(sgId);
            const secure = isSecureOrAbove(sgId);

            seq.push({
              moduleId: mod.id,
              moduleTitle: mod.title,
              unitId: unit.id,
              unitTitle: unit.title,
              lessonId: lesson.id,
              lessonTitle: lesson.title,
              topicTitle: sub.title || lesson.title,
              sectionGroupId: sgId,
              adaptive: !secure,
              startDifficulty: startDiff,
              maxQuestions: secure ? 1 : 2
            });
          }
        }
      }
    }

    // Fisher-Yates shuffle so topics from across the curriculum are interleaved.
    // Without this, early stopping always tests only the first few curriculum
    // topics (e.g. basic addition, place value) and never reaches harder topics.
    for (let i = seq.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [seq[i], seq[j]] = [seq[j], seq[i]];
    }

    return seq;
  }

  /* -----------------------------
     2. Build question queue
  ----------------------------- */
  async function buildQuestionQueue() {
    const items = generateDiagnosticSequence();

    // Single API call: fetch one question per (sgId, difficulty) for all SGs.
    // The Lambda returns { questions: { [sgId]: { easy, medium, hard } } }.
    const sgIds = [...new Set(items.map(item => item.sectionGroupId))];
    const { questions: questionMap } = await questionApi.getDiagnosticQuestions(sgIds);
    diagnosticQuestionMap.value = questionMap;

    const queue = [];

    for (const item of items) {
      const sgQuestions = questionMap[item.sectionGroupId] || {};
      // Try requested difficulty first, then fall back across difficulties
      const q = sgQuestions[item.startDifficulty]
             ?? sgQuestions['medium']
             ?? sgQuestions['easy']
             ?? sgQuestions['hard']
             ?? null;

      if (!q) continue;

      queue.push({
        ...item,
        difficulty: item.startDifficulty,
        slot: 1,
        question: q
      });
    }

    questionSequence.value = queue;
  }

  /* -----------------------------
     3. Start session
  ----------------------------- */
  async function start() {
    if (sessionStarted.value) return;

    // Ensure curriculum is loaded before building the queue
    if (!curriculumStore.isLoaded) {
      await curriculumStore.loadTrack("eplus_core");
    }

    await buildQuestionQueue();

    // Guard: if no questions were found, don't start
    if (questionSequence.value.length === 0) {
      return;
    }

    sessionStarted.value = true;
    sessionCompleted.value = false;
    currentIndex.value = 0;

    loadCurrentQuestion();
  }

  /* -----------------------------
     4. Reset (allow re-takes)
  ----------------------------- */
  function reset() {
    sessionStarted.value = false;
    sessionCompleted.value = false;
    confidence.value = 0;
    questionSequence.value = [];
    currentIndex.value = 0;
    currentQuestion.value = null;
    questionStartTime.value = null;
    responses.value = [];
    diagnosticQuestionMap.value = {};
  }

  /* -----------------------------
     5. Load current question
  ----------------------------- */
  function loadCurrentQuestion() {
    const q = questionSequence.value[currentIndex.value] || null;

    if (!q) {
      finishSession();
      return;
    }

    currentQuestion.value = q;
    questionStartTime.value = Date.now();
  }

  /* -----------------------------
     SUBMIT ANSWER (QUESTION-LEVEL)
  ------------------------------ */
  async function submitQuestionResult(result) {
    if (!currentQuestion.value) return;

    const q = currentQuestion.value;
    const now = Date.now();

    responses.value.push({
      childId,
      timestamp: now,
      moduleId: q.moduleId,
      unitId: q.unitId,
      lessonId: q.lessonId,
      sectionGroupId: q.sectionGroupId,
      difficulty: q.difficulty,
      correct: result.correct,
      containers: result.containers,
      timeTaken: Math.round((now - questionStartTime.value) / 1000)
    });

    updateConfidence(result.correct, q.difficulty);

    // Early stopping
    if (confidence.value >= EARLY_STOP_THRESHOLD) {
      finishSession();
      return;
    }

    // Adaptive branching: after slot 1, insert a follow-up from pre-fetched map
    if (q.slot === 1 && q.adaptive) {
      const nextDifficulty = result.correct ? "hard" : "easy";
      const followUp = diagnosticQuestionMap.value?.[q.sectionGroupId]?.[nextDifficulty];

      // Guard: don't reuse the same question as slot 1
      if (followUp && followUp._id !== q.question._id) {
        questionSequence.value.splice(currentIndex.value + 1, 0, {
          ...q,
          difficulty: nextDifficulty,
          slot: 2,
          question: followUp
        });
      }
    }

    currentIndex.value++;
    loadCurrentQuestion();
  }

  /* -----------------------------
     CONFIDENCE UPDATE
  ------------------------------ */
  function updateConfidence(correct, difficulty) {
    let delta = 0;

    if (difficulty === "easy")   delta = correct ? 8  : 5;
    if (difficulty === "medium") delta = correct ? 12 : 8;
    if (difficulty === "hard")   delta = correct ? 15 : 10;

    const damping = Math.max(0.3, 1 - confidence.value / 100);
    confidence.value = Math.min(100, confidence.value + delta * damping);
  }

  /* -----------------------------
     FINAL REPORT
  ------------------------------ */
  const diagnosticReport = computed(() => {
    if (!sessionCompleted.value) return null;

    const grouped = {};

    for (const r of responses.value) {
      if (!grouped[r.sectionGroupId]) grouped[r.sectionGroupId] = [];
      grouped[r.sectionGroupId].push(r);
    }

    const results = {};

    for (const sgId of Object.keys(grouped)) {
      const entries = grouped[sgId];
      let score = 0;

      for (const e of entries) {
        if (!e.correct) continue;
        if (e.difficulty === "easy") score += SCORING.easyCorrect;
        if (e.difficulty === "medium") score += SCORING.mediumCorrect;
        if (e.difficulty === "hard") score += SCORING.hardCorrect;
      }

      const levelObj =
        SCORING.levelMapping.find(m => score <= m.max) ||
        SCORING.levelMapping.at(-1);

      results[sgId] = {
        sectionGroupId: sgId,
        score,
        difficultyLevel: levelObj.difficultyLevel,
        attempts: entries.length,
        correctCount: entries.filter(e => e.correct).length
      };
    }

    return {
      results,
      responses: responses.value
    };
  });

  /* -----------------------------
     STUDENT LEVEL DERIVATION
  ------------------------------ */
  // Maps diagnostic responses to a 1–5 competency level used for
  // level-based lesson skipping.  Matches the lesson.level scale:
  //   1 = beginner  2 = developing  3 = secure  4 = confident  5 = mastery
  function deriveStudentLevel(resps) {
    if (!resps.length) return 1;
    const total = resps.length;
    const hardCorrect  = resps.filter(r => r.correct && r.difficulty === 'hard').length;
    const medCorrect   = resps.filter(r => r.correct && r.difficulty === 'medium').length;
    const totalCorrect = resps.filter(r => r.correct).length;
    const ratio = totalCorrect / total;

    if (hardCorrect / total >= 0.6)                       return 5; // mastery
    if (hardCorrect / total >= 0.3 || ratio >= 0.75)      return 4; // confident
    if (medCorrect  / total >= 0.4 || ratio >= 0.55)      return 3; // secure
    if (ratio >= 0.3)                                     return 2; // developing
    return 1;                                                        // beginner
  }

  /* -----------------------------
     COMMIT TO STORES
  ------------------------------ */
  function commitResults() {
    const report = diagnosticReport.value;
    if (!report) return;

    // Save diagnostic history
    assessmentStore.saveDiagnostic(childId, report);

    // Start with all directly-tested topics
    const expandedResults = { ...report.results };

    // Seed topics that were in the queue but not reached (early stopping).
    // Base the seed on actual correctness ratio, not confidence score —
    // confidence rises even with all-wrong answers so it's a poor proxy for ability.
    const totalAnswered = responses.value.length;
    const totalCorrect = responses.value.filter(r => r.correct).length;
    const correctRatio = totalAnswered > 0 ? totalCorrect / totalAnswered : 0;
    const untestedAccuracy = correctRatio >= 0.5 ? 0.45 : 0.40;

    for (const item of questionSequence.value) {
      const sgId = item.sectionGroupId;
      if (!expandedResults[sgId]) {
        expandedResults[sgId] = {
          sectionGroupId: sgId,
          score: 0,
          difficultyLevel: 'easy',
          attempts: 0,
          correctCount: 0,
          _seedAccuracy: untestedAccuracy,
        };
      }
    }

    // ── Level-based auto-seeding ──────────────────────────────────────────────
    // For each topic, resolve its effective level:
    //   sub.level  →  lesson.level  →  null (no auto-seeding)
    // If studentLevel > effectiveLevel the topic is elevated to secure (0.65)
    // so lessons the student is clearly above appear as completed in the journey.
    // Tested results (no _seedAccuracy) are never overwritten.
    const studentLevel = deriveStudentLevel(responses.value);

    for (const mod of curriculumStore.modules) {
      for (const unit of mod.units) {
        for (const lesson of unit.lessons) {
          for (const sub of (lesson.subsections || [])) {
            if (!sub.content) continue;

            // Topic level takes precedence over lesson level
            const effectiveLevel = sub.level ?? lesson.level ?? null;
            if (!effectiveLevel || studentLevel <= effectiveLevel) continue;

            const existing = expandedResults[sub.content];

            // Tested result (actual answer recorded) — never overwrite
            if (existing && existing._seedAccuracy === undefined) continue;

            // Untested or in-queue-but-not-reached — elevate to secure
            if (!existing) {
              expandedResults[sub.content] = {
                sectionGroupId: sub.content,
                score: 0,
                difficultyLevel: 'easy',
                attempts: 0,
                correctCount: 0,
                _seedAccuracy: 0.65,
              };
            } else if (existing._seedAccuracy < 0.65) {
              existing._seedAccuracy = 0.65;
            }
          }
        }
      }
    }
    // ─────────────────────────────────────────────────────────────────────────

    // Seed priors via store action — also persists to API and prevents
    // loadProgress() from overwriting the seeds when navigating to dashboard
    progressStore.seedFromDiagnostic(childId, expandedResults);
  }

  /* -----------------------------
     FINISH SESSION
  ------------------------------ */
  function finishSession() {
    sessionCompleted.value = true;
    commitResults();

    streakEngine.registerActivity(childId);

    progressStore.addActivity(childId, {
      type: "diagnostic",
      title: "Skills Check",
      score: confidence.value
    });

    track('skills_check_completed', {
      confidence_score: confidence.value,
      questions_answered: responses.value.length,
    });
  }

  /* -----------------------------
     STOP SESSION (early exit)
     Commits only directly-answered topics.
     Does NOT seed untested topics or apply level-based auto-seeding.
  ------------------------------ */
  function stopSession() {
    if (!sessionStarted.value || sessionCompleted.value) return;

    if (responses.value.length > 0) {
      // Build report from only the questions that were actually answered
      const grouped = {};
      for (const r of responses.value) {
        if (!grouped[r.sectionGroupId]) grouped[r.sectionGroupId] = [];
        grouped[r.sectionGroupId].push(r);
      }

      const results = {};
      for (const sgId of Object.keys(grouped)) {
        const entries = grouped[sgId];
        let score = 0;
        for (const e of entries) {
          if (!e.correct) continue;
          if (e.difficulty === 'easy')   score += SCORING.easyCorrect;
          if (e.difficulty === 'medium') score += SCORING.mediumCorrect;
          if (e.difficulty === 'hard')   score += SCORING.hardCorrect;
        }
        const levelObj = SCORING.levelMapping.find(m => score <= m.max) || SCORING.levelMapping.at(-1);
        results[sgId] = {
          sectionGroupId: sgId,
          score,
          difficultyLevel: levelObj.difficultyLevel,
          attempts: entries.length,
          correctCount: entries.filter(e => e.correct).length
        };
      }

      const partialReport = { results, responses: responses.value };
      assessmentStore.saveDiagnostic(childId, partialReport);
      progressStore.seedFromDiagnostic(childId, results);

      streakEngine.registerActivity(childId);
      progressStore.addActivity(childId, {
        type: "diagnostic",
        title: "Skills Check (Partial)",
        score: confidence.value
      });
    }

    sessionCompleted.value = true;
  }

  const currentTopicLabel = computed(() => {
    const q = questionSequence.value[currentIndex.value];
    return q?.topicTitle || q?.lessonTitle || '';
  });

  return {
    // state
    sessionStarted,
    sessionCompleted,
    currentQuestion,
    currentIndex,
    totalQuestions: computed(() => questionSequence.value.length),
    confidence,
    currentTopicLabel,
    estimatedDuration: ESTIMATED_DURATION_MINUTES,

    // actions
    start,
    reset,
    submitQuestionResult,
    stopSession,

    // output
    diagnosticReport
  };
}
